<template>
  <v-card id="rate-sheet-divergence">
    <v-card-title :class="headerClass">
      <v-icon color="tertiary" class="mr-2">mdi-note-text</v-icon>
      <span class="headline">{{ $t('activeRateSheets') }}</span>
      <v-spacer/>
      <BaseDialogActions @refresh="refresh"/>
    </v-card-title>
    <v-card-text class="pt-4">
      <v-row align="center" dense>
        <v-col cols="12" md="5">
          <div class="caption text--secondary">{{ $t('activeRateSheetsDescription') }}</div>
        </v-col>
        <v-spacer/>
        <v-col cols="12" md="3" v-if="destinations.length > 0">
          <Selector
          dense
          itemText="label"
          :items="destinations"
          :label="$t('destination')"
          :initialItem="initialDestination"
          prependInnerIcon="mdi-domain"
          elementId="rate-sheet-divergence-destination-selector"
          @item-selected="destinationSelected"/>
        </v-col>
        <v-col cols="12" md="3" v-if="destinations.length > 0 && isLogsMode">
          <Selector
          dense
          selectFirstOnChange
          itemText="tractTypeName"
          :items="tractTypes"
          :label="$t('tractType')"
          :equality="sameTractType"
          prependInnerIcon="mdi-target-variant"
          elementId="rate-sheet-divergence-tract-type-selector"
          @item-selected="tractType = $event"/>
        </v-col>
      </v-row>
      <v-row align="center" dense class="mt-2">
        <v-col cols="12" md="5">
          <v-text-field
          v-model="search"
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          id="rate-sheet-divergence-search"
          :label="$t('searchRateSheetDivergence')"/>
        </v-col>
        <v-spacer/>
        <v-col cols="auto" class="d-flex align-center flex-wrap">
          <Icon
          v-if="hasDifferences"
          icon="mdi-filter"
          margin="mr-3"
          elementId="rate-sheet-divergence-differences-only"
          :small="false"
          :iconColor="differencesOnly ? 'primary' : 'grey'"
          :tooltipText="$t('differencesOnly')"
          @icon-clicked="differencesOnly = !differencesOnly"/>
          <v-chip small outlined color="success" class="mr-2">
            <v-icon small left>mdi-check-circle</v-icon>
            {{ $t('instanceMatchCount', { count: totals.matchCount }) }}
          </v-chip>
          <v-chip v-if="hasDifferences" small outlined color="warning" class="mr-2">
            <v-icon small left>mdi-alert-circle</v-icon>
            {{ $t('instanceDifferCount', { count: totals.differCount }) }}
          </v-chip>
          <span class="caption text--secondary">{{ $tc('instancesAcrossDefinitions', totals.instanceCount, { definitions: groups.length }) }}</span>
        </v-col>
      </v-row>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4"/>
      <NoData v-else-if="destinations.length === 0" class="mt-4">{{ $t('noDestinationsAvailable') }}</NoData>
      <NoData v-else-if="groups.length === 0" class="mt-4">{{ $t('noActiveRateSheetsForDestination') }}</NoData>
      <div v-else class="mt-4">
        <v-expansion-panels
        v-for="group in groups"
        :key="group.definition.rateSheetDefinitionId"
        popout
        tile
        :value="openDefinitionIds.includes(group.definition.rateSheetDefinitionId) ? 0 : undefined"
        @change="togglePanel(group.definition.rateSheetDefinitionId, $event)">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-row no-gutters align="center">
                <v-col cols="auto" class="font-weight-medium">{{ group.definition.name }}</v-col>
                <v-spacer/>
                <v-col cols="auto" class="caption text--secondary mr-4">
                  <v-tooltip v-if="group.definition.latestVersion" bottom color="black">
                    <template #activator="{ on }">
                      <span v-on="on" class="mr-2">{{ formatDate(group.definition.latestVersion.effectiveDate) }}</span>
                    </template>
                    <span class="subtitle-1 white--text">{{ $t('rateSheetDefinitionVersionEffectiveDate') }}</span>
                  </v-tooltip>
                  <template v-else>
                    <Icon icon="mdi-alert-circle" iconColor="warning" :small="false"/>
                    <span class="warning--text mr-2">{{ $t('noEffectiveVersion') }}</span>
                  </template>
                  <v-chip v-if="group.definition.latestVersion" x-small outlined class="mr-2">
                    {{ group.definition.latestVersion.rateSheetDefinitionLines.length }} {{ $tc('lines', group.definition.latestVersion.rateSheetDefinitionLines.length) }}
                  </v-chip>
                  <v-chip x-small outlined color="success" class="mr-2">
                    <v-icon x-small left>mdi-check-circle</v-icon>
                    {{ $t('instanceMatchCount', { count: group.matchCount }) }}
                  </v-chip>
                  <v-chip v-if="group.differCount > 0" x-small outlined color="warning">
                    <v-icon x-small left>mdi-alert-circle</v-icon>
                    {{ $t('instanceDifferCount', { count: group.differCount }) }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
              dense
              hide-default-footer
              item-key="activityId"
              class="small-headers"
              :class="{ 'expandable-rows': group.definition.latestVersion !== null }"
              :show-expand="group.definition.latestVersion !== null"
              :headers="headersFor(group.definition)"
              :items="group.activities"
              :items-per-page="-1"
              :expanded="expandedIn(group)"
              @update:expanded="setExpanded(group, $event)"
              @click:row="(item, row) => group.definition.latestVersion !== null && row.expand(!row.isExpanded)">
                <template #item.status="{ item }">
                  <Icon
                  :icon="matchesDefinition(item, group.definition) ? 'mdi-check-circle' : 'mdi-alert-circle'"
                  :iconColor="matchesDefinition(item, group.definition) ? 'success' : 'warning'"
                  :tooltipText="differencesText(item, group.definition)"/>
                  <Icon
                  v-if="item.pendingScheduledRateChange"
                  icon="invoice-text-update"
                  iconColor="info"
                  :tooltipText="scheduledRateChangeTooltip(item.pendingScheduledRateChange)"/>
                </template>
                <template #item.setting="{ item }">{{ item.setting ?? $t('notApplicable') }}</template>
                <template #item.tractName="{ item }">{{ item.tractName ?? $t('notApplicable') }}</template>
                <template #item.loggerName="{ item }">{{ item.loggerName ?? $t('notApplicable') }}</template>
                <template #item.counterpartyName="{ item }">
                  <span v-if="counterpartyLabel(item)">
                    <Icon
                    v-if="item.runtimeCounterparty !== null"
                    iconColor="info"
                    icon="mdi-alpha-r-circle-outline"
                    :tooltipText="`${$t('runtimeCounterparty')}: ${RuntimeCounterparty.fromInt(item.runtimeCounterparty)}`"/>
                    {{ counterpartyLabel(item) }}
                  </span>
                  <span v-else>
                    {{ $t('notSet') }}
                    <Icon
                    v-if="item.runtimeCounterparty !== null"
                    icon="mdi-alert"
                    iconColor="error"
                    :tooltipText="$t('runtimeFieldNotSpecified', { runtimeField: RuntimeCounterparty.fromInt(item.runtimeCounterparty) })"/>
                  </span>
                </template>
                <template #item.effectiveDate="{ item }">{{ utcToLocalDate(item.effectiveDate) }}</template>
                <template #item.differences="{ item }">
                  <span :class="matchesDefinition(item, group.definition) ? 'success--text' : 'warning--text font-weight-medium'">
                    {{ differencesText(item, group.definition) }}
                  </span>
                </template>
                <template #item.contractExpirationDate="{ item }">
                  {{ item.contractExpirationDate ? utcToLocalDate(item.contractExpirationDate) : $t('notApplicable') }}
                </template>
                <template #item.actions="{ item }">
                  <Icon
                  icon="mdi-altimeter"
                  elementId="rate-sheet-divergence-view-contract"
                  margin=""
                  :tooltipText="$t('viewContract')"
                  @icon-clicked="viewContract(item)"/>
                </template>
                <template #expanded-item="{ headers: expandedHeaders, item }">
                  <td :colspan="expandedHeaders.length" class="pl-12 pr-4 py-4 grey lighten-5">
                    <RateSheetDivergenceLines
                    :definition="group.definition"
                    :instanceLines="instanceLinesByRateSheetId[item.rateSheetId]"/>
                  </td>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>
    <Dialog :stateId="contractDetailDialogId" @close="focusedContractId = undefined">
      <ContractDetail v-if="focusedContractId !== undefined" :contractId="focusedContractId"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { uniqBy } from 'lodash'
import { formatDate, utcToCompanyOffsetDstSensitive, utcToLocalDate } from '@/utils/DateFormatter'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { colorClassForContractMode, uniqueDialogId } from '@/utils/componentHelpers'
import { ContractMode, RuntimeCounterparty } from '@/utils/Enumerations'
import { groupDefinitions, matchesDefinition } from '@/components/contract/rate-sheet-divergence/RateSheetDivergence'

export default {
  name: 'RateSheetDivergence',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    RateSheetDivergenceLines: () => import('@/components/contract/rate-sheet-divergence/RateSheetDivergenceLines.vue'),
    Selector: () => import('@/components/core/Selector.vue')
  },

  props: {
    contractMode: {
      type: Object,
      required: true
    },
    includeProduction: {
      type: Boolean,
      required: true
    },
    includeWoodsSale: {
      type: Boolean,
      required: true
    },
    includeTransfer: {
      type: Boolean,
      required: true
    },
    includeLogYardSale: {
      type: Boolean,
      required: true
    }
  },

  data: () => ({
    RuntimeCounterparty,
    definitions: [],
    focusedContractId: undefined,
    contractDetailDialogId: uniqueDialogId('rate-sheet-divergence-contract-detail'),
    destination: undefined,
    tractType: undefined,
    storedDestinationAccountId: undefined,
    search: '',
    differencesOnly: false,
    loading: false,
    openDefinitionIds: [],
    autoOpenedDefinitionId: undefined,
    expandedActivities: [],
    instanceLinesByRateSheetId: {}
  }),

  computed: {
    ...mapGetters('contract', ['allContracts']),

    headerClass () {
      return colorClassForContractMode(this.contractMode.value)
    },

    isLogsMode () {
      return this.contractMode.value === ContractMode.Logs.value
    },

    destinations () {
      const destinations = new Map()

      for (const contract of this.allContracts) {
        if (destinations.has(contract.destinationAccountId)) continue
        destinations.set(contract.destinationAccountId, {
          accountId: contract.destinationAccountId,
          label: contract.destination
        })
      }

      return [...destinations.values()].sort((a, b) => a.label.localeCompare(b.label))
    },

    initialDestination () {
      return this.destinations.find(destination => destination.accountId === this.storedDestinationAccountId)
    },

    tractTypes () {
      const activities = this.definitions.flatMap(definition => definition.activities)
      const tractTypes = uniqBy(activities.filter(activity => activity.tractTypeId != null), 'tractTypeId')
        .map(({ tractTypeId, tractTypeName }) => ({ tractTypeId, tractTypeName }))
        .sort((a, b) => a.tractTypeName.localeCompare(b.tractTypeName))

      return [{ tractTypeId: undefined, tractTypeName: this.$t('allTractTypes') }, ...tractTypes]
    },

    visibleGroups () {
      return groupDefinitions(this.definitions, this.isVisible)
    },

    hasDifferences () {
      return this.visibleGroups.some(group => group.differCount > 0)
    },

    groups () {
      return this.differencesOnly && this.hasDifferences
        ? groupDefinitions(this.definitions, (activity, definition) => this.isVisible(activity, definition) && !matchesDefinition(activity, definition))
        : this.visibleGroups
    },

    totals () {
      return {
        instanceCount: this.groups.reduce((count, group) => count + group.activities.length, 0),
        matchCount: this.groups.reduce((count, group) => count + group.matchCount, 0),
        differCount: this.groups.reduce((count, group) => count + group.differCount, 0)
      }
    }

  },

  watch: {
    groups (groups) {
      if (groups.length !== 1) return

      const rateSheetDefinitionId = groups[0].definition.rateSheetDefinitionId
      if (rateSheetDefinitionId === this.autoOpenedDefinitionId) return

      this.autoOpenedDefinitionId = rateSheetDefinitionId
      this.openDefinitionIds = [rateSheetDefinitionId]
    }
  },

  created () {
    const stored = localStorage.getItem(LocalStorageKeys.RATE_SHEET_DIVERGENCE_DESTINATION_ID)
    const storedId = stored ? Number(JSON.parse(stored)) : undefined
    this.storedDestinationAccountId = Number.isFinite(storedId) ? storedId : undefined
  },

  methods: {
    ...mapActions('rate-sheet-definition', ['fetchRateSheetDivergence']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('dialog', ['openOrUpdateDialog']),
    formatDate,
    utcToLocalDate,
    matchesDefinition,

    destinationSelected (destination) {
      this.destination = destination
      localStorage.setItem(LocalStorageKeys.RATE_SHEET_DIVERGENCE_DESTINATION_ID, JSON.stringify(destination.accountId))
      this.refresh()
    },

    async refresh () {
      if (this.destination === undefined) return
      this.loading = true
      this.instanceLinesByRateSheetId = {}

      try {
        this.definitions = await this.fetchRateSheetDivergence({
          accountId: this.destination.accountId,
          contractTypes: {
            includeProduction: this.includeProduction,
            includeWoodsSale: this.includeWoodsSale,
            includeTransfer: this.includeTransfer,
            includeLogYardSale: this.includeLogYardSale
          }
        }) ?? []

        const expandedActivityIds = new Set(this.expandedActivities.map(activity => activity.activityId))
        this.expandedActivities = this.definitions
          .flatMap(definition => definition.activities)
          .filter(activity => expandedActivityIds.has(activity.activityId))
        this.expandedActivities.forEach(activity => this.loadInstanceLines(activity))
      } finally {
        this.loading = false
      }
    },

    sameTractType (a, b) {
      return a?.tractTypeId === b?.tractTypeId
    },

    viewContract ({ contractId }) {
      this.focusedContractId = contractId
      this.openOrUpdateDialog({ id: this.contractDetailDialogId, width: '80vw' })
    },

    counterpartyLabel ({ runtimeCounterparty, counterpartyName }) {
      if (counterpartyName) return counterpartyName
      if (runtimeCounterparty === RuntimeCounterparty.Driver.value || runtimeCounterparty === RuntimeCounterparty.Landowner.value) {
        return RuntimeCounterparty.fromInt(runtimeCounterparty)
      }
      return undefined
    },

    scheduledRateChangeTooltip ({ effectiveDate, requestedBy, note }) {
      const date = utcToCompanyOffsetDstSensitive(effectiveDate).format('MM/DD/YY')
      const tooltip = requestedBy
        ? this.$t('scheduledRateChangeEffectiveBy', { date, name: requestedBy })
        : this.$t('scheduledRateChangeEffective', { date })
      return note ? `${tooltip}: ${note}` : tooltip
    },

    togglePanel (rateSheetDefinitionId, value) {
      const withoutDefinition = this.openDefinitionIds.filter(id => id !== rateSheetDefinitionId)
      this.openDefinitionIds = value === 0 ? [...withoutDefinition, rateSheetDefinitionId] : withoutDefinition
    },

    differencesText (activity, definition) {
      if (definition.latestVersion === null) return this.$t('noEffectiveVersion')
      if (matchesDefinition(activity, definition)) return this.$t('matchesDefinition')

      return [
        activity.rateChangeCount > 0 ? this.$tc('rateChangeCount', activity.rateChangeCount) : undefined,
        activity.missingLineCount > 0 ? this.$tc('missingLineCount', activity.missingLineCount) : undefined,
        activity.extraLineCount > 0 ? this.$tc('extraLineCount', activity.extraLineCount) : undefined
      ].filter(Boolean).join(' · ')
    },

    isVisible (activity, definition) {
      if (this.tractType?.tractTypeId !== undefined && activity.tractTypeId !== this.tractType.tractTypeId) return false

      const term = (this.search ?? '').trim().toLocaleLowerCase()
      if (term === '') return true

      return [definition.name, activity.accountName, activity.setting, activity.tractName, activity.loggerName, this.counterpartyLabel(activity), activity.templateName]
        .some(value => value?.toLocaleLowerCase().includes(term))
    },

    headersFor (definition) {
      return [
        { text: this.$t('status'), value: 'status', align: 'center', sortable: false, width: 88 },
        { text: this.$t('contractAccount'), value: 'accountName' },
        this.isLogsMode ? { text: this.$t('tract'), value: 'tractName' } : undefined,
        this.isLogsMode ? { text: this.$t('logger'), value: 'loggerName' } : undefined,
        this.isLogsMode ? { text: this.$t('setting'), value: 'setting' } : undefined,
        { text: this.$t('counterparty'), value: 'counterpartyName' },
        { text: this.$t('activityTemplate'), value: 'templateName' },
        { text: this.$t('activatedOn'), value: 'effectiveDate' },
        { text: this.$t('differences'), value: 'differences', sortable: false },
        { text: this.$t('expires'), value: 'contractExpirationDate' },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'center', width: 80 },
        definition.latestVersion !== null ? { text: '', value: 'data-table-expand' } : undefined
      ].filter(Boolean)
    },

    expandedIn (group) {
      return this.expandedActivities.filter(activity => activity.rateSheetDefinitionId === group.definition.rateSheetDefinitionId)
    },

    setExpanded (group, expanded) {
      this.expandedActivities = [
        ...this.expandedActivities.filter(activity => activity.rateSheetDefinitionId !== group.definition.rateSheetDefinitionId),
        ...expanded
      ]
      expanded.forEach(activity => this.loadInstanceLines(activity))
    },

    async loadInstanceLines ({ activityId, rateSheetId }) {
      if (rateSheetId in this.instanceLinesByRateSheetId) return
      this.$set(this.instanceLinesByRateSheetId, rateSheetId, null)

      try {
        const rateSheet = await this.fetchRateSheet(rateSheetId)
        this.$set(this.instanceLinesByRateSheetId, rateSheetId, rateSheet.rateSheetLines)
      } catch {
        this.$delete(this.instanceLinesByRateSheetId, rateSheetId)
        this.expandedActivities = this.expandedActivities.filter(activity => activity.activityId !== activityId)
      }
    }
  }
}
</script>

<style scoped>
.expandable-rows ::v-deep tbody > tr:not(.v-data-table__expanded__content) {
  cursor: pointer;
}

::v-deep tbody > tr.v-data-table__expanded__row {
  background-color: #FAFAFA;
}
</style>
